import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Modal, Tabs } from 'antd'
import { useSelector } from 'react-redux'

import {
  ResumeAirRequestQuoteAirFares,
  ResumeAirRequestQuoteAirs
} from 'src/types'

import { ApplicationState } from 'src/store'
import { useT } from 'src/core/hooks/use-translation'

import { PickFareProps } from '.'
import SearchResultItem from '../SearchResultItem'

type Props = PickFareProps & {
  setPolicyListCheck(list: ResumeAirRequestQuoteAirs[]): void
}

function RoundTrip({ selectedFares, onPickFare, setPolicyListCheck }: Props) {
  const { air } = useSelector((state: ApplicationState) => ({
    air: state.air
  }))

  const searchKey = useMemo(() => {
    return Object.keys(air.searches)[0]
  }, [air.searches])

  const goingKey = searchKey || ''

  const returnKey = searchKey?.split('-').reverse().join('-') || ''

  const [activeKey, setActiveKey] = useState<string>(goingKey)

  const airBack = useMemo(() => {
    if (!air.searches[searchKey].list[1]) {
      return []
    }

    let fareSelected: ResumeAirRequestQuoteAirFares = null

    return air.searches[searchKey].list[1].request_quote_airs
      .filter(airFilter => {
        if (!selectedFares.length) {
          return true
        }

        const selectedAir = air.searches[
          searchKey
        ].list[0].request_quote_airs.find(airFind => {
          const fare = airFind.request_quote_air_fares.find(
            fareFind =>
              fareFind.request_quote_fare_hash ===
              selectedFares[0].request_quote_air_fare_hash[0]
          )

          if (fare) {
            fareSelected = fare
          }

          return !!fare
        })

        return !!selectedAir && selectedAir.company === airFilter.company
      })
      .map(airMap => ({
        ...airMap,
        request_quote_air_fares: airMap.request_quote_air_fares.filter(
          fareFilter => {
            if (!fareSelected) {
              return true
            }

            return (
              fareFilter.request_quote_hash ===
                fareSelected.request_quote_hash &&
              fareFilter.baggage === fareSelected.baggage &&
              fareFilter.family === fareSelected.family &&
              fareFilter.message_id === fareSelected.message_id
            )
          }
        )
      }))
      .filter(airFilter => !!airFilter.request_quote_air_fares.length)
  }, [air.searches, searchKey, selectedFares])

  const listGo = useMemo(
    () =>
      air.searches[searchKey].list[0]
        ? air.searches[searchKey].list[0].request_quote_airs
        : [],
    [air.searches, searchKey]
  )

  const { t } = useT()

  useEffect(() => {
    setPolicyListCheck(listGo)
  }, [setPolicyListCheck, listGo])

  useEffect(() => {
    if (selectedFares.length === 1) {
      setActiveKey(returnKey)
    }
  }, [selectedFares, returnKey])

  if (air.searches[searchKey].error) {
    return (
      <Alert
        closable={false}
        message={air.searches[searchKey].error}
        type="error"
      />
    )
  }

  if (!goingKey && !returnKey) {
    return <div />
  }

  return (
    <Tabs
      activeKey={activeKey}
      data-test-id="air-result-panel-card"
      defaultActiveKey={goingKey}
      onChange={activeKey => {
        setActiveKey(activeKey)

        if (activeKey === goingKey) {
          setPolicyListCheck(listGo)
        } else {
          setPolicyListCheck(airBack)
        }
      }}
    >
      <Tabs.TabPane
        data-test-id={`air-result-panel-${goingKey}-list`}
        tab={goingKey}
        key={goingKey}
      >
        <SearchResultItem
          selectedFares={selectedFares}
          onPickFare={onPickFare}
          searchKey={searchKey}
          list={listGo}
        />
      </Tabs.TabPane>
      <Tabs.TabPane
        data-test-id={`air-result-panel-${returnKey}-list`}
        tab={returnKey}
        key={returnKey}
      >
        <SearchResultItem
          selectedFares={selectedFares}
          onPickFare={(fare, searchKey) => {
            if (!selectedFares.length) {
              Modal.warning({
                content: t('web.services.airs.select-outward-flight'),
                title: t('common.attention'),
                onOk: () => setActiveKey(goingKey)
              })
              return
            }

            onPickFare(fare, searchKey)
          }}
          searchKey={searchKey}
          list={airBack}
        />
      </Tabs.TabPane>
    </Tabs>
  )
}

export default RoundTrip
